import { Alert, AlertNotification } from "@shared/models";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import {
  ActionWithPayload,
  AlertFilter,
  AlertNotificationFilter,
  PaginatedResponse,
  WithCallback,
} from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";

import api from "../api";
import { AlertReadDto, CreateAlertDto, CreateAlertSuccess } from "../interfaces";
import { createAlert, createUserAlert, getAlertNotifications, getAlerts, readAlertNotification } from "./actions";

function* getAlertsSaga({ payload }: ActionWithPayload<AlertFilter>) {
  try {
    yield put(startLoading());
    const response: Alert[] = yield call(api.getAlerts, payload);
    yield put(stopLoading());
    yield put(getAlerts.success(response));
  } catch (error) {
    yield put(getAlerts.failure(error as Error));
    yield put(stopLoading());
  }
}

function* getAlertNotificationsSaga({ payload }: ActionWithPayload<AlertNotificationFilter>) {
  try {
    yield put(startLoading());
    const { selector_type, ...restPayload } = payload;
    const response: PaginatedResponse<AlertNotification> = yield call(api.getAlertNotifications, restPayload);
    yield put(stopLoading());
    yield put(
      getAlertNotifications.success({ ...response, selector_type: selector_type || null, is_clear: !payload.page }),
    );
  } catch (error) {
    yield put(getAlertNotifications.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createAlertSaga({ payload }: ActionWithPayload<CreateAlertDto>) {
  try {
    yield put(startLoading());
    const response: CreateAlertSuccess = yield call(api.createAlert, payload);
    yield put(stopLoading());
    yield put(createAlert.success(response));
  } catch (error) {
    yield put(createAlert.failure(error as Error));
    yield put(stopLoading());
  }
}

function* createUserAlertSaga({ payload }: ActionWithPayload<CreateAlertDto & { id: number }>) {
  try {
    yield put(startLoading());
    const response: CreateAlertSuccess = yield call(api.createUserAlert, payload);
    yield put(stopLoading());
    yield put(createUserAlert.success(response));
  } catch (error) {
    yield put(createUserAlert.failure(error as Error));
    yield put(stopLoading());
  }
}

function* readAlertNotificationSaga({ payload }: ActionWithPayload<WithCallback<AlertReadDto>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: AlertNotification = yield call(api.readAlertNotification, rest);
    yield put(stopLoading());
    yield put(readAlertNotification.success(response));
    cb && cb();
  } catch (error) {
    yield put(readAlertNotification.failure(error as Error));
    yield put(stopLoading());
  }
}

function* alertSaga() {
  yield takeLatest(getAlerts.request, getAlertsSaga);
  yield takeEvery(getAlertNotifications.request, getAlertNotificationsSaga);
  yield takeLatest(createAlert.request, createAlertSaga);
  yield takeLatest(createUserAlert.request, createUserAlertSaga);
  yield takeLatest(readAlertNotification.request, readAlertNotificationSaga);
}

export default alertSaga;
